import React, { useMemo, useState } from 'react'
import { useField } from 'formik'
import ListItem from './Fields/ListItem'
import ListColor from './Fields/ListColor'
import ListTextArea from './Fields/ListTextArea'
import { TranslationPopoverProps, DetectionItem } from './helper'
import './AssetDetailsV2ExtendedFields.css'

interface BaseItem {
    confidence?: number;
    pixel_percentage?: number;
}

interface AssetDetailsV2ExtendedFieldsFieldConfig {
    uid: string;
    type: 'LIST_ITEM' | 'LIST_IMAGE_ITEM' | 'LIST_COLOR' | 'TEXT_AREA';
    title?: string;
    threshold?: boolean;
    threshold_value?: number;
    hidden?: boolean;
    checkbox?: boolean;
    banner?: boolean;
    banner_text?: string;
    highlight?: boolean;
    highlightOnClick?: () => void;
    enable_translation?: boolean;
    TranslationPopover?: (props: TranslationPopoverProps) => React.JSX.Element;
    getCheckboxResults?: (result: DetectionItem[]) => void;
    locked?: boolean;
    showConfidence?: boolean;
    hotspots?: boolean;
    canvasSize?: number;
    strokeColor?: string;
    translationLabel?: string;
}

interface PartialSelectResult {
    name: string;
    selected: boolean | null;
}

type SidebarProps = {
    field: AssetDetailsV2ExtendedFieldsFieldConfig;
    mainImage?: string;
    selectedThreshold?: number;
    showAll?: boolean;
    maxLimit?: number | null;
    highlightedIndex?: number | null;
    setPartialSelect?: (value: PartialSelectResult) => void;
    isTranslated?: boolean;
    setIsTranslated?: (value: boolean) => void;
    setHighlightedIndex?: (index: number | null) => void;
}

const AssetDetailsV2ExtendedFields = ({ 
    field, 
    mainImage, 
    selectedThreshold: externalThreshold,
    showAll: externalShowAll,
    maxLimit: externalMaxLimit,
    highlightedIndex: externalHighlightedIndex,
    setPartialSelect: externalSetPartialSelect,
    isTranslated: externalIsTranslated,
    setIsTranslated: externalSetIsTranslated,
    setHighlightedIndex: externalSetHighlightedIndex
}: SidebarProps) => {
    const [textField] = useField(field.uid);

    // internal state - backup if no external props provided
    const [internalThreshold] = useState<number>(field.threshold_value || 0);
    const [internalShowAll] = useState<boolean>(false);
    const [internalMaxLimit] = useState<number | null>(null);
    const [internalHighlightedIndex, setInternalHighlightedIndex] = useState<number | null>(null);
    const [internalPartialSelect, setInternalPartialSelect] = useState<PartialSelectResult>({ name: '', selected: null });
    const [internalIsTranslated, setInternalIsTranslated] = useState<boolean>(false);
    
    // Using external props if provided, otherwise using internal state
    const selectedThreshold = externalThreshold !== undefined 
        ? externalThreshold 
        : internalThreshold;
    const showAll = externalShowAll !== undefined 
        ? externalShowAll 
        : internalShowAll;
    const maxLimit = externalMaxLimit !== undefined 
        ? externalMaxLimit 
        : internalMaxLimit;
    const highlightedIndex = externalHighlightedIndex !== undefined 
        ? externalHighlightedIndex 
        : internalHighlightedIndex;
    const isTranslated = externalIsTranslated !== undefined 
        ? externalIsTranslated 
        : internalIsTranslated;
    
    const setPartialSelect = externalSetPartialSelect || setInternalPartialSelect;
    const setIsTranslated = externalSetIsTranslated || setInternalIsTranslated;
    const setHighlightedIndex = externalSetHighlightedIndex || setInternalHighlightedIndex;

    const sortedItems = useMemo(() => {
        if (field.type === 'TEXT_AREA' || !textField.value || !Array.isArray(textField.value)) {
            return textField.value;
        }

        return [...textField.value].sort((a: BaseItem, b: BaseItem) => {
            const aValue = a.confidence !== undefined ? a.confidence : 0;
            const bValue = b.confidence !== undefined ? b.confidence : 0;
            return bValue - aValue;
        });
    }, [textField.value, field.type]);

    // Apply filtering logic based on settings popover state
    const getFilteredItems = (): any => {
        if (!Array.isArray(sortedItems)) return sortedItems;
        
        let filtered = sortedItems;
        
        // if not showing all and hidden is true - applying threshold filter
        if (!showAll && field.hidden) {
            filtered = filtered.filter((item: BaseItem) => 
                (item.confidence !== undefined ? item.confidence : 0) >= selectedThreshold
            );
        }
        
        // applying max limit
        if (!showAll && maxLimit) {
            filtered = filtered.slice(0, maxLimit);
        } else if (showAll) {
            // When showing all, return all sorted items
            filtered = sortedItems;
        }
        
        return filtered;
    };

    const filteredItems = field.showConfidence ? getFilteredItems() : sortedItems;
        
    const renderField = () => {
        switch (field.type) {
            case 'LIST_ITEM':
                return (
                    <ListItem
                        field={field}
                        items={filteredItems}
                        threshold_value={selectedThreshold}
                        mainImage={mainImage}
                        updateValue={field.checkbox ? textField.onChange : null}
                        setPartialSelect={setPartialSelect}
                        highlightedIndex={highlightedIndex}
                        setHighlightedIndex={setHighlightedIndex}
                        isTranslated={isTranslated}
                        setIsTranslated={setIsTranslated}
                        handleCheckboxResult={() => field.getCheckboxResults?.(sortedItems)}
                    />
                )

            case 'LIST_IMAGE_ITEM':
                return (
                    <ListItem
                        field={field}
                        items={filteredItems}
                        threshold_value={selectedThreshold}
                        mainImage={mainImage}
                        setPartialSelect={setPartialSelect}
                        highlightedIndex={highlightedIndex}
                        setHighlightedIndex={setHighlightedIndex}
                        isTranslated={isTranslated}
                        setIsTranslated={setIsTranslated}
                        handleCheckboxResult={() => field.getCheckboxResults?.(sortedItems)}
                    />
                )

            case 'LIST_COLOR':
                return (
                    <ListColor
                        field={field}
                        bgColor={textField.value.length > 0 ? textField.value[0].bg_color : []}
                        dominantColors={textField.value.length > 0 ? textField.value[0].dominant_colors : []}
                        threshold_value={selectedThreshold}
                        showAll={showAll}
                    />
                )

            case 'TEXT_AREA':
                return (
                    <ListTextArea
                        field={field}
                        item={textField.value.length > 0 ? textField.value[0] : { text: '' }}
                        updateValue={textField.onChange}
                        isTranslated={isTranslated}
                        setIsTranslated={setIsTranslated}
                    />
                )

            default:
                return null
        }
    }

    let fieldToRender = renderField();

    return fieldToRender ? <div className='asset-details-v2-extended-fields-container'>
        {fieldToRender}
    </div> : null;
}

export default AssetDetailsV2ExtendedFields